import { useMemo } from 'react';
import { logger } from '@ui/utils/logger';
import type { FilterGroup } from '../types/filter-panel.types';
import {
    useGetConditionTypeValuesQuery,
    useGetDiagnosisValuesQuery,
    useGetEthnicityValuesQuery,
    useGetFilterMetadataQuery,
    useGetGenderValuesQuery,
    useGetMedicationClassValuesQuery,
    useGetMedicationNameValuesQuery,
    useGetMeasurementTypeValuesQuery,
    useGetNoteTypeValuesQuery,
    useGetProcedureTypeValuesQuery,
    useGetRaceValuesQuery,
} from '../api/patientFiltersApi';

// Value providers for dynamic selects
const GENDERS = [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
    { label: 'Other', value: 'Other' },
];
const RACES = [
    { label: 'White', value: 'White' },
    { label: 'Black or African American', value: 'Black or African American' },
    { label: 'Asian', value: 'Asian' },
    { label: 'American Indian or Alaska Native', value: 'American Indian or Alaska Native' },
    { label: 'Native Hawaiian or Other Pacific Islander', value: 'Native Hawaiian or Other Pacific Islander' },
    { label: 'Unknown', value: 'Unknown' },
];
const ETHNICITIES = [
    { label: 'Hispanic or Latino', value: 'Hispanic or Latino' },
    { label: 'Not Hispanic or Latino', value: 'Not Hispanic or Latino' },
    { label: 'Unknown', value: 'Unknown' },
];

export const useFilterMetadataAndValues = (searchTerms: Record<string, string>, filterGroups?: FilterGroup[]) => {
    const { data: metadata, isLoading: metadataLoading } = useGetFilterMetadataQuery();

    // Extract used fields from filterGroups
    const usedFields = useMemo(() => {
        if (!filterGroups) return new Set<string>();
        const fields = new Set<string>();
        filterGroups.forEach(group => {
            group.conditions.forEach(condition => {
                if (condition.field) {
                    fields.add(condition.field);
                }
            });
        });
        return fields;
    }, [filterGroups]);

    // Only fetch values for fields that are actually used in conditions
    const hasGender = usedFields.has('gender');
    const hasRace = usedFields.has('race');
    const hasEthnicity = usedFields.has('ethnicity');
    const hasDiagnosis = usedFields.has('diagnosis');
    const hasNoteType = usedFields.has('note_type');
    const hasMedicationName = usedFields.has('medication_name');
    const hasMedicationClass = usedFields.has('medication_class');
    const hasProcedureType = usedFields.has('procedure_type');
    const hasConditionType = usedFields.has('condition_type');
    const hasMeasurementType = usedFields.has('measurement_type');

    const { data: genderValues } = useGetGenderValuesQuery(
        hasGender && searchTerms.gender ? { search: searchTerms.gender } : undefined,
        { skip: !hasGender }
    );
    const { data: raceValues } = useGetRaceValuesQuery(
        hasRace && searchTerms.race ? { search: searchTerms.race } : undefined,
        { skip: !hasRace }
    );
    const { data: ethnicityValues } = useGetEthnicityValuesQuery(
        hasEthnicity && searchTerms.ethnicity ? { search: searchTerms.ethnicity } : undefined,
        { skip: !hasEthnicity }
    );
    const { data: diagnosisValues } = useGetDiagnosisValuesQuery(
        hasDiagnosis && searchTerms.diagnosis
            ? { search: searchTerms.diagnosis, limit: 100 }
            : hasDiagnosis
              ? { limit: 100 }
              : undefined,
        { skip: !hasDiagnosis }
    );
    const { data: noteTypeValues } = useGetNoteTypeValuesQuery(
        hasNoteType && searchTerms.note_type ? { search: searchTerms.note_type } : undefined,
        { skip: !hasNoteType }
    );
    const { data: medicationValues } = useGetMedicationNameValuesQuery(
        hasMedicationName && searchTerms.medication_name
            ? { search: searchTerms.medication_name, limit: 100 }
            : hasMedicationName
              ? { limit: 100 }
              : undefined,
        { skip: !hasMedicationName }
    );
    const { data: medicationClassValues } = useGetMedicationClassValuesQuery(
        hasMedicationClass && searchTerms.medication_class ? { search: searchTerms.medication_class } : undefined,
        { skip: !hasMedicationClass }
    );
    const { data: procedureValues } = useGetProcedureTypeValuesQuery(
        hasProcedureType && searchTerms.procedure_type
            ? { search: searchTerms.procedure_type, limit: 100 }
            : hasProcedureType
              ? { limit: 100 }
              : undefined,
        { skip: !hasProcedureType }
    );
    const { data: conditionTypeValues } = useGetConditionTypeValuesQuery(
        hasConditionType && searchTerms.condition_type ? { search: searchTerms.condition_type } : undefined,
        { skip: !hasConditionType }
    );
    const { data: measurementTypeValues } = useGetMeasurementTypeValuesQuery(
        hasMeasurementType && searchTerms.measurement_type
            ? { search: searchTerms.measurement_type, limit: 100 }
            : hasMeasurementType
              ? { limit: 100 }
              : undefined,
        { skip: !hasMeasurementType }
    );

    // Memoize filter values map to avoid recalculating on every render
    const filterValuesMap = useMemo<Record<string, any>>(() => {
        const map = {
            gender: genderValues?.values ?? GENDERS,
            race: raceValues?.values ?? RACES,
            ethnicity: ethnicityValues?.values ?? ETHNICITIES,
            diagnosis: diagnosisValues?.values,
            note_type: noteTypeValues?.values,
            medication_name: medicationValues?.values,
            medication_class: medicationClassValues?.values,
            procedure_type: procedureValues?.values,
            condition_type: conditionTypeValues?.values,
            measurement_type: measurementTypeValues?.values,
        };

        return map;
    }, [
        genderValues?.values,
        raceValues?.values,
        ethnicityValues?.values,
        diagnosisValues?.values,
        noteTypeValues?.values,
        medicationValues?.values,
        medicationClassValues?.values,
        procedureValues?.values,
        conditionTypeValues?.values,
        measurementTypeValues?.values,
    ]);

    // Group diagnosis options by ICD code prefix
    const diagnosisGroupedOptions = useMemo(() => {
        const values = diagnosisValues?.values;
        if (!values) return [];

        // Group diagnosis by ICD code prefix
        const groups: Record<
            string,
            Array<{ label: string; value: string; icd_code: string; count?: number; concept_id?: number }>
        > = {};

        values.forEach((v: any) => {
            if (!v.icd_code) return;

            // Extract prefix (e.g., "I23" from "I23.1" or "I23")
            const icdPrefix = v.icd_code.split('.')[0];

            if (!groups[icdPrefix]) {
                groups[icdPrefix] = [];
            }

            const label = v.label ?? v.value ?? v.icd_code;
            groups[icdPrefix].push({
                label,
                value: v.icd_code,
                icd_code: v.icd_code,
                count: v.count,
                concept_id: v.concept_id,
            });
        });

        // Convert to tree structure
        const treeStructure = Object.entries(groups).map(([prefix, items]) => {
            logger.debug('Filter metadata items', { prefix, itemsCount: items.length });
            // Find common title from items' labels
            let groupTitle = prefix; // Default to ICD prefix

            if (items.length > 0) {
                const firstLabel = items[0].label;
                // Try to find common words at the start of labels
                const words = firstLabel.split(' ');
                let commonPrefix = '';

                for (let i = 0; i < words.length; i++) {
                    const testPrefix = words.slice(0, i + 1).join(' ');
                    const allMatch = items.every(item => item.label.toLowerCase().startsWith(testPrefix.toLowerCase()));
                    if (allMatch) {
                        commonPrefix = testPrefix;
                    } else {
                        break;
                    }
                }

                // Use common prefix if found (at least 2 words), otherwise use first item's label
                if (commonPrefix && commonPrefix.split(' ').length >= 2) {
                    groupTitle = commonPrefix;
                } else if (firstLabel) {
                    // Use first item's label, but remove ICD code part if present
                    groupTitle = firstLabel.split('(')[0].trim();
                }
            }

            // Add ICD code prefix in parentheses
            const finalTitle = `${groupTitle} (${prefix})`;

            return {
                title: finalTitle,
                key: `group-${prefix}`,
                children: items.map((item, index) => {
                    const label = `${item.label} (${item.icd_code})${item.count ? ` (${item.count})` : ''}`;
                    return {
                        title: label,
                        key: `${item.value}-${index}`,
                        isLeaf: true,
                        icdValue: item.value,
                        conceptId: item.concept_id,
                        icdLabel: label, // Store label for easy access
                    };
                }),
            };
        });

        return treeStructure;
    }, [diagnosisValues?.values]);

    // Build grouped options by category from metadata
    const groupedFieldOptions = useMemo(() => {
        if (!metadata?.filters) return [];
        const byCat: Record<string, any[]> = {};
        metadata.filters.forEach(f => {
            if (!byCat[f.category]) byCat[f.category] = [];
            byCat[f.category].push(f);
        });
        return Object.entries(byCat).map(([cat, filters]) => ({
            label: cat,
            options: filters.map(f => ({ label: f.label, value: f.field })),
        }));
    }, [metadata]);

    return {
        metadata,
        metadataLoading,
        filterValuesMap,
        diagnosisGroupedOptions,
        groupedFieldOptions,
    };
};
